import { GameConfig, DefaultGameConfig } from '@/types';

/**
 * Centralized timer configuration for the Home Run game
 * This ensures consistent timer behavior across all game components
 */

export interface TimerConfig {
  questionPhaseSeconds: number; // Time for reading/thinking about question
  runningPhaseSeconds: number;  // Time for players to move to answer zones
}

/**
 * Default timer configuration used when no teacher customization is available
 */
export const DEFAULT_TIMER_CONFIG: TimerConfig = {
  questionPhaseSeconds: 10, // Default 10 seconds for question phase
  runningPhaseSeconds: 12,  // Default 12 seconds for running phase
};

/**
 * Alternative timer configurations for different game modes
 */
export const TIMER_PRESETS = {
  quick: {
    questionPhaseSeconds: 5,
    runningPhaseSeconds: 8,
  },
  standard: {
    questionPhaseSeconds: 10,
    runningPhaseSeconds: 12,
  },
  extended: {
    questionPhaseSeconds: 15,
    runningPhaseSeconds: 15,
  },
} as const;

/**
 * Extract timer configuration from GameConfig (teacher-customized)
 */
export function getTimerConfigFromGameConfig(gameConfig: GameConfig): TimerConfig {
  return {
    questionPhaseSeconds: gameConfig.timePerQuestion ?? DEFAULT_TIMER_CONFIG.questionPhaseSeconds,
    runningPhaseSeconds: gameConfig.timePerAnswer ?? DEFAULT_TIMER_CONFIG.runningPhaseSeconds,
  };
}

/**
 * Extract timer configuration from DefaultGameConfig
 */
export function getTimerConfigFromDefaultConfig(defaultConfig: DefaultGameConfig): TimerConfig {
  return {
    questionPhaseSeconds: defaultConfig.timePerQuestion,
    runningPhaseSeconds: defaultConfig.timePerAnswer,
  };
}

/**
 * Get timer configuration with fallback to defaults
 */
export function getTimerConfig(
  gameConfig?: GameConfig | DefaultGameConfig | null
): TimerConfig {
  if (!gameConfig) {
    console.log('⏱️ Using default timer configuration');
    return DEFAULT_TIMER_CONFIG;
  }

  // Check if it's a GameConfig (has optional timer properties)
  if ('timePerQuestion' in gameConfig && 'timePerAnswer' in gameConfig) {
    const timerConfig = {
      questionPhaseSeconds: gameConfig.timePerQuestion ?? DEFAULT_TIMER_CONFIG.questionPhaseSeconds,
      runningPhaseSeconds: gameConfig.timePerAnswer ?? DEFAULT_TIMER_CONFIG.runningPhaseSeconds,
    };
    console.log('⏱️ Using teacher-configured timer settings:', timerConfig);
    return timerConfig;
  }

  // It's a DefaultGameConfig (has required timer properties)
  if ('timePerQuestion' in gameConfig && 'timePerAnswer' in gameConfig &&
      typeof gameConfig.timePerQuestion === 'number' && typeof gameConfig.timePerAnswer === 'number') {
    const timerConfig = {
      questionPhaseSeconds: gameConfig.timePerQuestion,
      runningPhaseSeconds: gameConfig.timePerAnswer,
    };
    console.log('⏱️ Using default game configuration timer settings:', timerConfig);
    return timerConfig;
  }

  console.log('⏱️ Fallback to default timer configuration');
  return DEFAULT_TIMER_CONFIG;
}

/**
 * Validate timer configuration values
 */
export function validateTimerConfig(config: TimerConfig): boolean {
  const { questionPhaseSeconds, runningPhaseSeconds } = config;

  // Check for reasonable bounds (1 second minimum, 5 minutes maximum)
  if (questionPhaseSeconds < 1 || questionPhaseSeconds > 300) {
    console.error(`❌ Invalid question phase timer: ${questionPhaseSeconds}s (must be 1-300s)`);
    return false;
  }

  if (runningPhaseSeconds < 1 || runningPhaseSeconds > 300) {
    console.error(`❌ Invalid running phase timer: ${runningPhaseSeconds}s (must be 1-300s)`);
    return false;
  }

  console.log('✅ Timer configuration is valid:', config);
  return true;
}

/**
 * Create a safe timer configuration with bounds checking
 */
export function createSafeTimerConfig(
  questionPhaseSeconds: number,
  runningPhaseSeconds: number
): TimerConfig {
  const config: TimerConfig = {
    questionPhaseSeconds: Math.max(1, Math.min(300, questionPhaseSeconds)),
    runningPhaseSeconds: Math.max(1, Math.min(300, runningPhaseSeconds)),
  };

  if (!validateTimerConfig(config)) {
    console.warn('⚠️ Timer configuration validation failed, using defaults');
    return DEFAULT_TIMER_CONFIG;
  }

  return config;
}